// components/CalculatorForm.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Divider,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Business,
  AccessTime,
  AttachMoney,
  Calculate,
  PictureAsPdf,
  Save,
  Cancel,
  DateRange,
  DragIndicator,
  SwapVert
} from '@mui/icons-material';
import CompanyInfoSection from './CompanyInfoSection';
import PercentageSection from './PercentageSection';
import ResultsSection from './ResultsSection';
import FinalAmountAdjuster from './FinalAmountAdjuster';
import SummarySection from './SummarySection';
import { axiosInstance } from '../../utils/axios';
import {generatePDF} from '../../utils/pdfGenerator';

const IVA_RATE = 0.21;

// Definir todos los meses disponibles
const MESES_DISPONIBLES = [
  { key: 'ene', label: 'Enero', order: 1 },
  { key: 'feb', label: 'Febrero', order: 2 },
  { key: 'mar', label: 'Marzo', order: 3 },
  { key: 'abr', label: 'Abril', order: 4 },
  { key: 'may', label: 'Mayo', order: 5 },
  { key: 'jun', label: 'Junio', order: 6 },
  { key: 'jul', label: 'Julio', order: 7 },
  { key: 'ago', label: 'Agosto', order: 8 },
  { key: 'sep', label: 'Septiembre', order: 9 },
  { key: 'oct', label: 'Octubre', order: 10 },
  { key: 'nov', label: 'Noviembre', order: 11 },
  { key: 'dic', label: 'Diciembre', order: 12 }
];

const CalculatorForm = ({ editingCalculation = null, onSaveComplete = null, onCancel = null }) => {
  const [formData, setFormData] = useState({
    nombreEmpresa: '',
    horasServicio: '',
    montoBase: '',
    porcentajeEne: '',
    porcentajeFeb: '',
    porcentajeMar: '',
    porcentajeAbr: '',
    porcentajeMay: '',
    porcentajeJun: '',
    porcentajeJul: '',
    porcentajeAgo: '',
    porcentajeSep: '',
    porcentajeOct: '',
    porcentajeNov: '',
    porcentajeDic: '',
    clienteId: null
  });

  // Nuevo estado para meses seleccionados
  const [mesesSeleccionados, setMesesSeleccionados] = useState(['sep', 'oct', 'nov', 'dic', 'ene']); // Por defecto los meses originales

  // Estado para el orden personalizado de los meses
  const [ordenMeses, setOrdenMeses] = useState([9, 10, 11, 12, 1]); // [sep, oct, nov, dic, ene] por defecto

  // Estado para drag and drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Estado para controlar si estamos en proceso de carga inicial
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [calculatedData, setCalculatedData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showIVA, setShowIVA] = useState(true);
  const [montoFinalDeseado, setMontoFinalDeseado] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [savedCalculationId, setSavedCalculationId] = useState(null);
  const [savingCalculation, setSavingCalculation] = useState(false);

  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Función para mostrar notificaciones
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Funciones para drag and drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Agregar un poco de transparencia al elemento arrastrado
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    // Reordenar el array de ordenMeses
    const newOrdenMeses = [...ordenMeses];
    const draggedItem = newOrdenMeses[draggedIndex];
    
    // Remover el elemento arrastrado
    newOrdenMeses.splice(draggedIndex, 1);
    
    // Insertar en la nueva posición
    newOrdenMeses.splice(dropIndex, 0, draggedItem);
    
    setOrdenMeses(newOrdenMeses);
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    // Auto-recalcular después del reordenamiento
    setTimeout(() => {
      calcular();
      showSnackbar('Orden actualizado y recalculado automáticamente.', 'success');
    }, 100);
  };

  // Función para limpiar el formulario
const clearForm = () => {
  setIsLoadingData(true);
  
  setFormData(prev => ({
    // Limpiar datos del cliente
    nombreEmpresa: '',
    horasServicio: '',
    montoBase: '',
    clienteId: null,
    // MANTENER todos los porcentajes
    porcentajeEne: prev.porcentajeEne,
    porcentajeFeb: prev.porcentajeFeb,
    porcentajeMar: prev.porcentajeMar,
    porcentajeAbr: prev.porcentajeAbr,
    porcentajeMay: prev.porcentajeMay,
    porcentajeJun: prev.porcentajeJun,
    porcentajeJul: prev.porcentajeJul,
    porcentajeAgo: prev.porcentajeAgo,
    porcentajeSep: prev.porcentajeSep,
    porcentajeOct: prev.porcentajeOct,
    porcentajeNov: prev.porcentajeNov,
    porcentajeDic: prev.porcentajeDic,
  }));
  
  // MANTENER los meses seleccionados y el orden
  // No resetear mesesSeleccionados ni ordenMeses
  
  setCalculatedData(null);
  setShowResults(false);
  setMontoFinalDeseado('');
  setSavedCalculationId(null);
  
  setTimeout(() => {
    setIsLoadingData(false);
  }, 100);
};

  // Función para cargar datos de edición
  const loadEditingData = (calculation) => {
    setIsLoadingData(true); // Marcar que estamos cargando datos
    
    setFormData({
      nombreEmpresa: calculation.nombre_empresa || '',
      horasServicio: calculation.horas_servicio?.toString() || '',
      montoBase: calculation.monto_base?.toString() || '',
      porcentajeEne: calculation.porcentaje_ene?.toString() || '',
      porcentajeFeb: calculation.porcentaje_feb?.toString() || '',
      porcentajeMar: calculation.porcentaje_mar?.toString() || '',
      porcentajeAbr: calculation.porcentaje_abr?.toString() || '',
      porcentajeMay: calculation.porcentaje_may?.toString() || '',
      porcentajeJun: calculation.porcentaje_jun?.toString() || '',
      porcentajeJul: calculation.porcentaje_jul?.toString() || '',
      porcentajeAgo: calculation.porcentaje_ago?.toString() || '',
      porcentajeSep: calculation.porcentaje_sep?.toString() || '',
      porcentajeOct: calculation.porcentaje_oct?.toString() || '',
      porcentajeNov: calculation.porcentaje_nov?.toString() || '',
      porcentajeDic: calculation.porcentaje_dic?.toString() || '',
      clienteId: calculation.cliente_id
    });
    
    // Cargar el orden personalizado si existe, sino usar por defecto
    if (calculation.orden && Array.isArray(calculation.orden)) {
      setOrdenMeses(calculation.orden);
      // Convertir el orden a meses seleccionados
      const mesesFromOrden = calculation.orden.map(num => 
        MESES_DISPONIBLES.find(m => m.order === num)?.key
      ).filter(Boolean);
      setMesesSeleccionados(mesesFromOrden);
    } else if (typeof calculation.orden === 'string') {
      // Si orden viene como string JSON, parsearlo
      try {
        const ordenParsed = JSON.parse(calculation.orden);
        setOrdenMeses(ordenParsed);
        const mesesFromOrden = ordenParsed.map(num => 
          MESES_DISPONIBLES.find(m => m.order === num)?.key
        ).filter(Boolean);
        setMesesSeleccionados(mesesFromOrden);
      } catch (e) {
        console.error('Error parsing orden JSON:', e);
        // Usar método de detección de meses activos como fallback
        loadMesesFromData(calculation);
      }
    } else {
      // Determinar qué meses están activos basado en los datos (método anterior)
      loadMesesFromData(calculation);
    }
    
    setSavedCalculationId(calculation.id);
    
    // Auto-calcular con los datos cargados después de un delay
    setTimeout(() => {
      setIsLoadingData(false); // Marcar que terminamos de cargar
      calcular();
    }, 200);
  };

  // Función helper para cargar meses desde datos
  const loadMesesFromData = (calculation) => {
    const mesesActivos = [];
    const ordenActivo = [];
    MESES_DISPONIBLES.forEach(mes => {
      const valor = calculation[`porcentaje_${mes.key}`];
      if (valor && parseFloat(valor) > 0) {
        mesesActivos.push(mes.key);
        ordenActivo.push(mes.order);
      }
    });
    
    if (mesesActivos.length === 0) {
      setMesesSeleccionados(['sep', 'oct', 'nov', 'dic', 'ene']);
      setOrdenMeses([9, 10, 11, 12, 1]);
    } else {
      setMesesSeleccionados(mesesActivos);
      setOrdenMeses(ordenActivo);
    }
  };

  // Efecto para cargar datos cuando se está editando
  useEffect(() => {
    if (editingCalculation) {
      loadEditingData(editingCalculation);
    } else {
      clearForm();
    }
  }, [editingCalculation]);

  // Efecto para recalcular cuando cambia el orden de meses
  useEffect(() => {
    // Solo recalcular si:
    // 1. No estamos cargando datos iniciales
    // 2. Ya hay datos calculados 
    // 3. Hay meses seleccionados
    if (!isLoadingData && calculatedData && calculatedData.montoBase && mesesSeleccionados.length > 0) {
      console.log('Orden de meses cambió (no durante carga), recalculando...');
      calcular();
    }
  }, [ordenMeses]); // Se ejecuta cuando ordenMeses cambia

  // Función para manejar el cambio de meses seleccionados
  const handleMesesChange = (event) => {
    const value = event.target.value;
    const newMesesSeleccionados = typeof value === 'string' ? value.split(',') : value;
    setMesesSeleccionados(newMesesSeleccionados);
    
    // Actualizar el orden basado en los meses seleccionados
    // Mantener el orden existente para los meses que ya estaban, agregar nuevos al final
    const newOrden = [];
    
    // Primero, mantener el orden de los meses que ya estaban seleccionados
    ordenMeses.forEach(num => {
      const mes = MESES_DISPONIBLES.find(m => m.order === num);
      if (mes && newMesesSeleccionados.includes(mes.key)) {
        newOrden.push(num);
      }
    });
    
    // Luego, agregar los meses nuevos al final en orden cronológico
    newMesesSeleccionados.forEach(mesKey => {
      const mes = MESES_DISPONIBLES.find(m => m.key === mesKey);
      if (mes && !newOrden.includes(mes.order)) {
        newOrden.push(mes.order);
      }
    });
    
    setOrdenMeses(newOrden);
    
    // Limpiar porcentajes de meses no seleccionados
    const newFormData = { ...formData };
    MESES_DISPONIBLES.forEach(mes => {
      if (!newMesesSeleccionados.includes(mes.key)) {
        newFormData[`porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = '';
      }
    });
    setFormData(newFormData);
  };

  // Función para calcular aumentos
  const calcular = () => {
    const { nombreEmpresa, horasServicio, montoBase } = formData;
    const montoBaseNum = parseFloat(montoBase) || 0;

    if (!montoBaseNum || montoBaseNum <= 0) {
      showSnackbar('Por favor ingrese un monto base válido mayor a 0', 'error');
      setCalculatedData(null);
      setShowResults(false);
      return;
    }

    if (mesesSeleccionados.length === 0) {
      showSnackbar('Por favor seleccione al menos un mes para calcular', 'error');
      setCalculatedData(null);
      setShowResults(false);
      return;
    }

    // Usar el orden personalizado en lugar del orden cronológico
    const mesesOrdenados = ordenMeses.map(num => 
      MESES_DISPONIBLES.find(m => m.order === num)
    ).filter(Boolean);

    // Calcular aumentos progresivos según el orden personalizado
    const resultados = {};
    const porcentajesAcumulados = {};
    let porcentajeAcumulativo = 0;
    let montoFinal = montoBaseNum;

    mesesOrdenados.forEach((mes, index) => {
      const fieldName = `porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`;
      const porcentajeMes = parseFloat(formData[fieldName]) || 0;
      porcentajeAcumulativo += porcentajeMes;
      
      const montoMes = montoBaseNum * (1 + porcentajeAcumulativo / 100);
      
      // Guardar resultados con nombres compatibles con ResultsSection
      resultados[`monto${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = montoMes;
      resultados[`porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = porcentajeMes;
      porcentajesAcumulados[`porcentajeAcum${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = porcentajeAcumulativo;
      
      montoFinal = montoMes;
    });

    // Para mantener compatibilidad con ResultsSection, agregamos los campos originales si existen
    if (mesesSeleccionados.includes('sep')) {
      resultados.montoSep = resultados.montoSep;
      resultados.porcentajeSep = resultados.porcentajeSep;
      porcentajesAcumulados.porcentajeAcumSep = porcentajesAcumulados.porcentajeAcumSep;
    }
    if (mesesSeleccionados.includes('oct')) {
      resultados.montoOct = resultados.montoOct;
      resultados.porcentajeOct = resultados.porcentajeOct;
      porcentajesAcumulados.porcentajeAcumOct = porcentajesAcumulados.porcentajeAcumOct;
    }
    if (mesesSeleccionados.includes('nov')) {
      resultados.montoNov = resultados.montoNov;
      resultados.porcentajeNov = resultados.porcentajeNov;
      porcentajesAcumulados.porcentajeAcumNov = porcentajesAcumulados.porcentajeAcumNov;
    }
    if (mesesSeleccionados.includes('dic')) {
      resultados.montoDic = resultados.montoDic;
      resultados.porcentajeDic = resultados.porcentajeDic;
      porcentajesAcumulados.porcentajeAcumDic = porcentajesAcumulados.porcentajeAcumDic;
    }
    if (mesesSeleccionados.includes('ene')) {
      resultados.montoEne = resultados.montoEne;
      resultados.porcentajeEne = resultados.porcentajeEne;
      porcentajesAcumulados.porcentajeAcumEne = porcentajesAcumulados.porcentajeAcumEne;
    }

    const calculated = {
      nombreEmpresa,
      horasServicio,
      montoBase: montoBaseNum,
      mesesSeleccionados: mesesSeleccionados,
      mesesOrdenados: mesesOrdenados,
      ordenMeses: ordenMeses, // Incluir el orden personalizado
      montoFinal: montoFinal,
      porcentajeTotal: porcentajeAcumulativo,
      clienteId: formData.clienteId,
      ...resultados,
      ...porcentajesAcumulados
    };

    console.log('Calculated data:', calculated); // Para debugging
    console.log('Orden personalizado usado:', ordenMeses); // Para debugging
    console.log('Meses ordenados para cálculo:', mesesOrdenados.map(m => m.label)); // Para debugging

    setCalculatedData(calculated);
    setMontoFinalDeseado(montoFinal.toFixed(2));
    setShowResults(true);
    
    // Si estamos editando, no resetear el saved ID
    if (!editingCalculation) {
      setSavedCalculationId(null);
    }

    showSnackbar('Cálculo realizado exitosamente', 'success');
  };

  // Función para guardar el cálculo en la base de datos
  const saveCalculationToDatabase = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      throw new Error('No hay datos de cálculo para guardar');
    }

    if (!formData.clienteId) {
      throw new Error('Debe seleccionar un cliente');
    }

    try {
      const dataToSave = {
        cliente_id: formData.clienteId,
        nombre_empresa: calculatedData.nombreEmpresa,
        horas_servicio: parseFloat(calculatedData.horasServicio) || 0,
        monto_base: calculatedData.montoBase,
        orden: ordenMeses, // Agregar el orden personalizado
        // Incluir todos los porcentajes (enviar 0 para los no seleccionados)
        porcentaje_ene: parseFloat(formData.porcentajeEne) || 0,
        porcentaje_feb: parseFloat(formData.porcentajeFeb) || 0,
        porcentaje_mar: parseFloat(formData.porcentajeMar) || 0,
        porcentaje_abr: parseFloat(formData.porcentajeAbr) || 0,
        porcentaje_may: parseFloat(formData.porcentajeMay) || 0,
        porcentaje_jun: parseFloat(formData.porcentajeJun) || 0,
        porcentaje_jul: parseFloat(formData.porcentajeJul) || 0,
        porcentaje_ago: parseFloat(formData.porcentajeAgo) || 0,
        porcentaje_sep: parseFloat(formData.porcentajeSep) || 0,
        porcentaje_oct: parseFloat(formData.porcentajeOct) || 0,
        porcentaje_nov: parseFloat(formData.porcentajeNov) || 0,
        porcentaje_dic: parseFloat(formData.porcentajeDic) || 0
      };

      let response;
      
      if (editingCalculation && savedCalculationId) {
        // Actualizar cálculo existente
        response = await axiosInstance.put(`/calculo/${savedCalculationId}`, dataToSave);
        showSnackbar('Cálculo actualizado exitosamente', 'success');
      } else {
        // Crear nuevo cálculo
        response = await axiosInstance.post('/calculo', dataToSave);
        setSavedCalculationId(response.data.id);
        showSnackbar('Cálculo guardado exitosamente', 'success');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al guardar cálculo:', error);
      
      // En caso de error de conexión, simular guardado local
      if (error.code === 'NETWORK_ERROR') {
        const localId = savedCalculationId || `local_${Date.now()}`;
        setSavedCalculationId(localId);
        showSnackbar('Cálculo guardado localmente (sin conexión a API)', 'warning');
        return { id: localId };
      }
      
      throw error;
    }
  };

  // Función para guardar solo el cálculo (sin PDF)
  const handleSaveCalculation = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      showSnackbar('Por favor realice primero el cálculo antes de guardar', 'error');
      return;
    }

    if (!formData.clienteId) {
      showSnackbar('Debe seleccionar un cliente para guardar el cálculo', 'error');
      return;
    }

    setSavingCalculation(true);

    try {
      const savedData = await saveCalculationToDatabase();
      
      // Si es un callback de edición, notificar al componente padre
      if (onSaveComplete) {
        onSaveComplete(savedData);
      }
      
      // Si es un nuevo cálculo, limpiar el formulario
      if (!editingCalculation) {
        setTimeout(() => {
          clearForm();
          showSnackbar('Formulario limpiado para nuevo cálculo', 'info');
        }, 2000);
      }
      
    } catch (error) {
      if (error.message.includes('cliente')) {
        showSnackbar('Error: Debe seleccionar un cliente válido', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Error: Datos inválidos. Verifique la información', 'error');
      } else {
        showSnackbar('Error al guardar el cálculo', 'error');
      }
    } finally {
      setSavingCalculation(false);
    }
  };

  // Función mejorada para generar PDF que también guarda en BD
  const handleGeneratePDF = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      showSnackbar('Por favor realice primero el cálculo antes de generar el PDF', 'error');
      return;
    }

    if (!formData.clienteId) {
      showSnackbar('Debe seleccionar un cliente para generar el PDF', 'error');
      return;
    }

    setGeneratingPDF(true);

    try {
      // 1. Primero guardar en la base de datos
      showSnackbar('Guardando cálculo en la base de datos...', 'info');
      const savedData = await saveCalculationToDatabase();
      
      // 2. Luego generar el PDF
      showSnackbar('Generando PDF...', 'info');
      await generatePDF(calculatedData, () => {});
      
      // 3. Mostrar confirmación de éxito
      showSnackbar(
        `PDF generado y cálculo guardado exitosamente (ID: ${savedData.id})`, 
        'success'
      );

      // 4. Si es callback de edición, notificar al componente padre
      if (onSaveComplete) {
        onSaveComplete(savedData);
      }
      
      // 5. Si es un nuevo cálculo, limpiar el formulario después de un tiempo
      if (!editingCalculation) {
        setTimeout(() => {
          clearForm();
          showSnackbar('Formulario limpiado para nuevo cálculo', 'info');
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error en el proceso:', error);
      
      if (error.message.includes('cliente')) {
        showSnackbar('Error: Debe seleccionar un cliente válido', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Error: Datos inválidos. Verifique la información', 'error');
      } else {
        // Si falla el guardado pero queremos generar PDF de todas formas
        try {
          await generatePDF(calculatedData, () => {});
          showSnackbar('PDF generado, pero no se pudo guardar en la base de datos', 'warning');
        } catch (pdfError) {
          showSnackbar('Error al generar el PDF', 'error');
        }
      }
    } finally {
      setGeneratingPDF(false);
    }
  };

  const ajustarMontoFinal = () => {
    const montoFinalDeseadoNum = parseFloat(montoFinalDeseado);
    
    if (!montoFinalDeseadoNum || montoFinalDeseadoNum <= calculatedData.montoBase) {
      showSnackbar('Por favor ingrese un monto final válido (mayor al monto base)', 'error');
      return;
    }

    // Calcular el nuevo porcentaje total necesario
    const nuevoPorcentajeTotal = ((montoFinalDeseadoNum - calculatedData.montoBase) / calculatedData.montoBase) * 100;
    
    // Mantener las proporciones originales de los porcentajes
    const porcentajeOriginalTotal = calculatedData.porcentajeTotal;
    
    if (porcentajeOriginalTotal === 0) {
      showSnackbar('No hay porcentajes configurados para ajustar', 'error');
      return;
    }
    
    const factor = nuevoPorcentajeTotal / porcentajeOriginalTotal;
    
    // Calcular nuevos porcentajes manteniendo proporciones
    const newFormData = { ...formData };
    mesesSeleccionados.forEach(mesKey => {
      const fieldName = `porcentaje${mesKey.charAt(0).toUpperCase() + mesKey.slice(1)}`;
      const valorActual = parseFloat(formData[fieldName]) || 0;
      const nuevoValor = valorActual * factor;
      newFormData[fieldName] = nuevoValor.toFixed(2);
    });

    setFormData(newFormData);
    showSnackbar('Porcentajes ajustados. Recalcule para ver los nuevos resultados.', 'info');
  };

  const isEditMode = !!editingCalculation;

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {isEditMode ? '✏️ Editar Cálculo' : '💰 Calculadora de Aumentos Progresivos'}
          </Typography>
          
          {isEditMode && onCancel && (
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={onCancel}
              color="inherit"
            >
              Cancelar Edición
            </Button>
          )}
        </Box>

        {isEditMode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Editando cálculo ID: {savedCalculationId}. Los cambios se guardarán cuando presione "Guardar" o "Generar PDF".
          </Alert>
        )}
        
        <CompanyInfoSection formData={formData} setFormData={setFormData} />

        {/* Sección de selección de meses */}
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateRange />
            📅 Seleccionar Meses para Aumentos
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Meses a incluir en el cálculo</InputLabel>
            <Select
              multiple
              value={mesesSeleccionados}
              onChange={handleMesesChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const mes = MESES_DISPONIBLES.find(m => m.key === value);
                    return (
                      <Chip 
                        key={value} 
                        label={mes?.label || value} 
                        size="small"
                        color="primary"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {MESES_DISPONIBLES.map((mes) => (
                <MenuItem key={mes.key} value={mes.key}>
                  {mes.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            💡 Seleccione los meses donde desea aplicar aumentos. Podrá reordenarlos arrastrando y soltando.
          </Typography>
        </Paper>

        {/* Explicación */}
        <Card sx={{ mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 ¿Cómo funcionan los aumentos progresivos?
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Los porcentajes se acumulan según el orden que usted defina:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              Puede personalizar completamente el orden de aplicación de los aumentos usando drag & drop.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Ejemplo: Si configura Dic-Ene-Feb con 10% cada mes, en febrero tendrá un 30% de aumento total.
            </Typography>
          </CardContent>
        </Card>

        {/* Sección de porcentajes con Drag & Drop */}
        {mesesSeleccionados.length > 0 && (
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">
                📈 Porcentajes de Aumento por Mes
              </Typography>
              <Tooltip title="Arrastra y suelta para reordenar">
                <SwapVert color="action" />
              </Tooltip>
            </Box>
            
            <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
              🔄 Orden de aplicación: {ordenMeses.map((num, index) => {
                const mes = MESES_DISPONIBLES.find(m => m.order === num);
                return `${index + 1}. ${mes?.label || num}`;
              }).join(' → ')}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              💡 <strong>Tip:</strong> Arrastra los campos por el ícono de líneas para cambiar el orden de aplicación de los aumentos
            </Alert>
            
            <Grid container spacing={3}>
              {ordenMeses
                .map(num => MESES_DISPONIBLES.find(m => m.order === num))
                .filter(Boolean)
                .map((mes, index) => {
                  const fieldName = `porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`;
                  const isDraggedOver = dragOverIndex === index;
                  const isBeingDragged = draggedIndex === index;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={`${mes.key}-${index}`}>
                      <Card
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        sx={{
                          cursor: 'grab',
                          transition: 'all 0.2s ease',
                          transform: isBeingDragged ? 'rotate(5deg) scale(0.95)' : 'none',
                          boxShadow: isDraggedOver ? 4 : 1,
                          border: isDraggedOver ? '2px dashed #1976d2' : '1px solid transparent',
                          backgroundColor: isDraggedOver ? 'action.hover' : 'background.paper',
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'translateY(-2px)'
                          },
                          '&:active': {
                            cursor: 'grabbing'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <DragIndicator 
                              sx={{ 
                                color: 'action.active',
                                cursor: 'grab'
                              }} 
                            />
                            <Typography variant="h6" sx={{ 
                              fontSize: '1rem',
                              fontWeight: 600,
                              color: index === 0 ? 'primary.main' : 'text.primary'
                            }}>
                              {index + 1}. {mes.label}
                            </Typography>
                          </Box>
                          
                          <TextField
                            fullWidth
                            label={`Porcentaje de aumento`}
                            type="number"
                            value={formData[fieldName]}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [fieldName]: e.target.value
                            }))}
                            InputProps={{
                              endAdornment: '%',
                            }}
                            inputProps={{
                              min: 0,
                              step: 0.01
                            }}
                            helperText={`Se aplicará en posición ${index + 1}`}
                            sx={{
                              '& .MuiInputLabel-root': {
                                color: index === 0 ? 'primary.main' : 'text.secondary'
                              }
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              🎯 Los porcentajes se aplicarán en el orden mostrado arriba. Use drag & drop para reordenar fácilmente.
            </Typography>
          </Paper>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={<Calculate />}
          onClick={calcular}
          sx={{ mt: 3 }}
          disabled={mesesSeleccionados.length === 0}
        >
          🔢 {isEditMode ? 'Recalcular' : 'Calcular Aumentos'}
        </Button>
      </Paper>

      {/* Resultados */}
      {showResults && calculatedData && (
        <>
          <ResultsSection 
            calculatedData={calculatedData} 
            showIVA={showIVA} 
            setShowIVA={setShowIVA}
            IVA_RATE={IVA_RATE}
          />

          <FinalAmountAdjuster 
            montoFinalDeseado={montoFinalDeseado}
            setMontoFinalDeseado={setMontoFinalDeseado}
            ajustarMontoFinal={ajustarMontoFinal}
          />

          <SummarySection calculatedData={calculatedData} IVA_RATE={IVA_RATE} />

          {/* Botones de acción */}
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={savingCalculation ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSaveCalculation}
                  disabled={savingCalculation || !formData.clienteId}
                  sx={{ minWidth: 200 }}
                >
                  {savingCalculation ? 'Guardando...' : (isEditMode ? '💾 Actualizar Cálculo' : '💾 Guardar Cálculo')}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                  onClick={handleGeneratePDF}
                  disabled={generatingPDF || !formData.clienteId}
                  sx={{ minWidth: 200 }}
                >
                  {generatingPDF ? 'Generando PDF...' : '📄 Generar PDF'}
                </Button>
              </Box>

              {/* Alertas de estado */}
              {!formData.clienteId && (
                <Alert severity="warning">
                  Debe seleccionar un cliente para poder guardar el cálculo y generar PDF
                </Alert>
              )}

              {savedCalculationId && !isEditMode && (
                <Alert severity="success">
                  ✅ Cálculo guardado exitosamente en la base de datos (ID: {savedCalculationId})
                </Alert>
              )}

              {!isEditMode && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  💡 Después de guardar o generar PDF, el formulario se limpiará automáticamente
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CalculatorForm;